/**
 * Centralized utility for converting technical error messages to user-friendly messages.
 * This ensures consistent, non-technical error messaging across the application.
 */

/**
 * Maps technical error messages to user-friendly messages
 */
export const getUserFriendlyError = (error: string): string => {
  const errorLower = error.toLowerCase();
  
  // Registration-specific errors (check these first for specificity)
  if (errorLower.includes('already registered') || errorLower.includes('user already exists')) {
    return 'An account with this email already exists. Please sign in instead.';
  }
  if (errorLower.includes('invalid email') || errorLower.includes('email format')) {
    return 'Please enter a valid email address.';
  }
  if (errorLower.includes('email not confirmed') || errorLower.includes('verify your email')) {
    return 'Please check your email and click the verification link before signing in.';
  }
  if (errorLower.includes('phone') && (errorLower.includes('invalid') || errorLower.includes('format'))) {
    return 'Please enter a valid phone number.';
  }
  if (errorLower.includes('signup') && errorLower.includes('disabled')) {
    return 'Registration is temporarily unavailable. Please try again later.';
  }
  if (errorLower.includes('password') && (errorLower.includes('weak') || errorLower.includes('requirement'))) {
    return 'Your password does not meet security requirements. Please use at least 8 characters with uppercase, lowercase, numbers, and special characters.';
  }
  
  // Authentication errors
  if (errorLower.includes('database error') || errorLower.includes('failed to verify')) {
    return "We couldn't verify your account. Please try signing in again or contact support if this continues.";
  }
  if (errorLower.includes('no active session') || errorLower.includes('session')) {
    return 'Your session has expired. Please sign in again.';
  }
  
  // Payment and Stripe errors
  if (errorLower.includes('card_declined') || errorLower.includes('declined')) {
    return 'Your payment was declined. Please try a different payment method.';
  }
  if (errorLower.includes('insufficient_funds') || errorLower.includes('insufficient funds')) {
    return 'Your card has insufficient funds. Please try a different payment method.';
  }
  if (errorLower.includes('invalid_card') || errorLower.includes('invalid card')) {
    return 'The card information is invalid. Please check your details and try again.';
  }
  if (errorLower.includes('expired_card') || errorLower.includes('card expired')) {
    return 'Your card has expired. Please use a different card.';
  }
  if (errorLower.includes('processing_error')) {
    return 'There was an issue processing your payment. Please try again.';
  }
  if (errorLower.includes('payment method not created') || errorLower.includes('payment_method')) {
    return 'There was an issue processing your card. Please try again.';
  }
  if (errorLower.includes('failed to initialize payment') || errorLower.includes('setup')) {
    return "We couldn't set up the payment form. Please refresh the page and try again.";
  }
  
  // Network and API errors
  if (errorLower.includes('edge function') || errorLower.includes('function error')) {
    return 'Our servers are temporarily unavailable. Please try again in a moment.';
  }
  if (errorLower.includes('network') || errorLower.includes('fetch failed') || errorLower.includes('failed to fetch')) {
    return 'Please check your internet connection and try again.';
  }
  if (errorLower.includes('timeout') || errorLower.includes('timed out')) {
    return 'The request took too long. Please try again.';
  }
  if (errorLower.includes('rate limit') || errorLower.includes('too many')) {
    return 'Too many requests. Please wait a moment and try again.';
  }
  
  // 2FA errors
  if (errorLower.includes('2fa') || errorLower.includes('two-factor') || errorLower.includes('verification failed')) {
    return "We couldn't verify your code. Please check it and try again.";
  }
  if (errorLower.includes('failed to load 2fa') || errorLower.includes('2fa settings')) {
    return "We couldn't load your security settings. Please try again.";
  }
  
  // Property/offer errors
  if (errorLower.includes('failed to submit offer') || errorLower.includes('offer')) {
    return "We couldn't submit your offer. Please try again or contact support.";
  }
  if (errorLower.includes('not found')) {
    return "We couldn't find the requested information. Please try again.";
  }
  
  // Promo code errors
  if (errorLower.includes('promo') || errorLower.includes('validate')) {
    return "We couldn't validate this code. Please check it and try again.";
  }
  
  // Generic fallback
  return 'Something went wrong. Please try again or contact support if the problem continues.';
};

/**
 * Returns a user-friendly error title based on the error type
 */
export const getUserFriendlyErrorTitle = (error: string): string => {
  const errorLower = error.toLowerCase();
  
  if (errorLower.includes('database') || errorLower.includes('profile') || errorLower.includes('verify')) {
    return 'Account Issue';
  }
  if (errorLower.includes('payment') || errorLower.includes('card') || errorLower.includes('stripe')) {
    return 'Payment Issue';
  }
  if (errorLower.includes('session') || errorLower.includes('expired')) {
    return 'Session Expired';
  }
  if (errorLower.includes('network') || errorLower.includes('timeout') || errorLower.includes('connection')) {
    return 'Connection Issue';
  }
  if (errorLower.includes('2fa') || errorLower.includes('verification')) {
    return 'Verification Issue';
  }
  
  return 'Something Went Wrong';
};

/**
 * Helper specifically for payment errors - provides more contextual messages
 */
export const getPaymentErrorMessage = (error: string): string => {
  const errorLower = error.toLowerCase();
  
  if (errorLower.includes('session')) {
    return 'Your session has expired. Please sign in again.';
  }
  if (errorLower.includes('declined')) {
    return 'Your payment was declined. Please try a different card.';
  }
  if (errorLower.includes('insufficient')) {
    return 'Your card has insufficient funds.';
  }
  if (errorLower.includes('invalid') || errorLower.includes('card')) {
    return 'Please check your card details and try again.';
  }
  if (errorLower.includes('expired')) {
    return 'Your card has expired. Please use a different card.';
  }
  
  return "We couldn't process your payment. Please try again or contact support.";
};
